import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import {
    fetchApsConnection,
    fetchApsHubs,
    fetchApsHubProjects,
    linkAccProject,
    unlinkAccProject,
    disconnectAps,
} from '../../../../flux/actions/apsThunks';
import { clearApsPickers, clearApsActionError } from '../../../../flux/stores/projectsReducer';
import { isAlreadyLinked, isAccLinkBroken } from '../../../../flux/facades/apsApi';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { useApsConnectPopup } from '../../../../utils/useApsConnectPopup';
import './ApsConnectionPanel.css';

const EMPTY = {
    status: 'idle',
    connection: null,
    error: null,
    hubs: { data: [], isLoading: false, error: null },
    accProjects: { hubId: null, data: [], isLoading: false, error: null },
    action: { isBusy: false, error: null },
};

function formatDate(value) {
    if (!value) return 'an unknown date';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? 'an unknown date' : date.toLocaleString();
}

/** Autodesk failures and our own get very different wording. */
function apsErrorMessage(error) {
    if (!error) return null;
    if (error.status === 403) return 'Only organization admins can manage the Autodesk connection.';
    if (error.status === 502) return 'Autodesk is not responding right now. Please try again in a moment.';
    if (error.status === 422) {
        return 'That hub or ACC project is no longer available to the connected Autodesk account. Refresh the list and pick again.';
    }
    return error.message || 'The Autodesk request failed.';
}

/**
 * Names are best-effort: null means Autodesk did not give one, or could not be
 * read at all. The id is always there once something is picked, so fall back to
 * it instead of rendering a blank.
 */
function displayRef(ref) {
    if (!ref) return 'Unknown';
    return ref.name || ref.id;
}

/**
 * What to tell an admin about a link that no longer resolves.
 *
 * The wording tracks the API's own distinctions, which are not cosmetic. A
 * denial says the credential is fine and the permission is not, so re-picking
 * the same thing is useless. A "not found" says Autodesk will not distinguish
 * "deleted" from "never visible to you", so it is explicitly not a promise that
 * the credential is healthy and must not be reported as one.
 */
const BROKEN_LINK_COPY = {
    hub_access_denied: {
        title: 'No access to this hub',
        body: 'The connected Autodesk account exists and is working, but Autodesk will not grant it this hub. Picking the same hub again will not help. Give the account access to the hub in ACC, or reconnect using an account that already has it.',
    },
    hub_not_found: {
        title: 'This hub could not be found',
        body: 'Autodesk has no such hub for the connected account. It may have been deleted, or it may never have been visible to this account. Autodesk does not say which. Pick a hub again.',
    },
    project_access_denied: {
        title: 'No access to this ACC project',
        body: 'The hub resolved, but Autodesk will not grant the connected account this project inside it. Picking the same project again will not help. Give the account access to the project in ACC, or reconnect using an account that already has it.',
    },
    project_not_found: {
        title: 'This ACC project could not be found',
        body: 'The hub resolved, but Autodesk has no such project inside it for the connected account. It may have been deleted, or it may never have been visible to this account. Pick a project again.',
    },
};

export const ApsConnectionPanel = ({ projectId, currentUserObjectId }) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const aps = useAppSelector(state => state.projects.aps[projectId]) ?? EMPTY;
    const { connect, isConnecting, error: popupError } = useApsConnectPopup(projectId);

    const [isPicking, setPicking] = useState(false);
    const [hubId, setHubId] = useState('');
    const [accProjectId, setAccProjectId] = useState('');
    const [replacePrompt, setReplacePrompt] = useState(false);
    const [isConfirmingDisconnect, setConfirmingDisconnect] = useState(false);

    // Guard the two Autodesk-backed lists against duplicate in-flight requests.
    const hubsRequestedRef = useRef(false);
    const accProjectsRequestedRef = useRef(null);

    const withToken = useCallback(async (run) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return null;
            return await run(accessToken);
        } catch (error) {
            console.error('Autodesk request failed:', error);
            return null;
        }
    }, [getAccessToken]);

    useEffect(() => {
        withToken(accessToken => dispatch(fetchApsConnection({ accessToken, projectId })));
    }, [dispatch, projectId, withToken]);

    // The picker lists are snapshots taken with one access token. Dropping them
    // on unmount stops a stale hub list being offered against a refreshed one.
    useEffect(() => () => { dispatch(clearApsPickers(projectId)); }, [dispatch, projectId]);

    const connection = aps.connection;
    const isRevoked = connection?.status === 'revoked';
    const accLink = connection?.acc_link;
    // `unlinked` is the one state that means nothing is stored to point at.
    // Every other state, broken ones included, has a pick worth showing.
    const isLinked = Boolean(accLink && accLink.state !== 'unlinked');
    const linkBroken = isAccLinkBroken(accLink?.state);
    // Not a verdict on the pick, so it gets a neutral note rather than the
    // repair banner. The API is explicit that a client must not call it broken.
    const linkUnverified = accLink?.state === 'unverified';
    const isConnected = aps.status === 'loaded' && connection && !isRevoked;
    const showPicker = isConnected && (!isLinked || isPicking);

    // Both lists exist only to be picked from. The names of an *already linked*
    // hub and project come down on the connection itself, resolved server-side
    // against a short-lived cache and seeded at link time. So opening this tab
    // on a linked project costs no Autodesk round trip at all, and a page view
    // never fails because Autodesk is down.
    useEffect(() => {
        if (!showPicker) return;
        if (aps.hubs.data.length > 0 || aps.hubs.isLoading || aps.hubs.error) return;
        if (hubsRequestedRef.current) return;
        hubsRequestedRef.current = true;
        withToken(accessToken => dispatch(fetchApsHubs({ accessToken, projectId })));
    }, [showPicker, aps.hubs, dispatch, projectId, withToken]);

    useEffect(() => {
        if (!showPicker || !hubId) return;
        if (aps.accProjects.hubId === hubId) return;
        // The token fetch leaves an async gap before the pending action records
        // the hub, so without this the effect can fire twice for one hub.
        if (accProjectsRequestedRef.current === hubId) return;
        accProjectsRequestedRef.current = hubId;
        withToken(accessToken =>
            dispatch(fetchApsHubProjects({ accessToken, projectId, hubId }))
        );
    }, [showPicker, hubId, aps.accProjects.hubId, dispatch, projectId, withToken]);

    const startPicking = () => {
        dispatch(clearApsActionError(projectId));
        setReplacePrompt(false);
        setHubId(accLink?.hub?.id ?? '');
        setAccProjectId('');
        setPicking(true);
    };

    const cancelPicking = () => {
        setPicking(false);
        setReplacePrompt(false);
        setHubId('');
        setAccProjectId('');
    };

    const submitLink = async (allowReplace) => {
        const result = await withToken(accessToken =>
            dispatch(linkAccProject({ accessToken, projectId, hubId, accProjectId, allowReplace }))
        );
        if (!result) return;
        if (linkAccProject.fulfilled.match(result)) {
            cancelPicking();
            return;
        }
        // Asking first, rather than sending allowReplace up front, also covers
        // the case where another admin linked this project while the picker
        // was open.
        setReplacePrompt(isAlreadyLinked(result.payload ?? {}));
    };

    const handleUnlink = () => withToken(accessToken =>
        dispatch(unlinkAccProject({ accessToken, projectId }))
    );

    const handleDisconnect = async () => {
        const result = await withToken(accessToken =>
            dispatch(disconnectAps({ accessToken, projectId }))
        );
        if (result && disconnectAps.fulfilled.match(result)) {
            setConfirmingDisconnect(false);
            cancelPicking();
            // The reducer emptied both lists. Clear the guards too, or a later
            // reconnect would find them still marked as fetched and show
            // nothing to pick.
            hubsRequestedRef.current = false;
            accProjectsRequestedRef.current = null;
        }
    };

    if (aps.status === 'idle' || aps.status === 'loading') {
        return <p className="projects-loading">Checking the Autodesk connection…</p>;
    }

    if (aps.status === 'error') {
        return <p className="org-error">{apsErrorMessage(aps.error)}</p>;
    }

    const actionError = apsErrorMessage(aps.action.error);

    return (
        <div className="pd-aps">
            {popupError && <p className="org-error">{popupError}</p>}

            {aps.status === 'notConnected' && (
                <div className="pd-aps-state">
                    <h3 className="pd-aps-title">Not connected to Autodesk</h3>
                    <p className="pd-note">
                        Connect this project to Autodesk Construction Cloud to link it to an ACC
                        project. You will sign in to Autodesk and grant access once.
                    </p>
                    <button type="button" className="rules-btn-primary" onClick={connect} disabled={isConnecting}>
                        {isConnecting ? 'Waiting for Autodesk…' : 'Connect to Autodesk'}
                    </button>
                </div>
            )}

            {isRevoked && (
                <div className="pd-aps-state">
                    <h3 className="pd-aps-title">Reconnect to Autodesk</h3>
                    <p className="pd-note">
                        The stored Autodesk access is no longer valid. This is routine: Autodesk
                        expires access after about two weeks without use, and disconnecting has the
                        same effect. Sign in again to restore it.
                    </p>
                    {isLinked && (
                        <p className="pd-note">
                            The linked ACC project is remembered, so you will not need to pick it again.
                        </p>
                    )}
                    <button type="button" className="rules-btn-primary" onClick={connect} disabled={isConnecting}>
                        {isConnecting ? 'Waiting for Autodesk…' : 'Reconnect'}
                    </button>
                </div>
            )}

            {isConnected && (
                <div className="pd-aps-state">
                    <h3 className="pd-aps-title">
                        {isLinked ? 'Linked to an ACC project' : 'Connected, not yet linked'}
                    </h3>

                    <dl className="pd-facts">
                        <div className="pd-fact">
                            <dt>Connected</dt>
                            <dd>
                                {formatDate(connection.connected_at)}
                                {connection.connected_by === currentUserObjectId ? ' by you' : ' by another admin'}
                            </dd>
                        </div>
                        {connection.scope && (
                            <div className="pd-fact">
                                <dt>Granted access</dt>
                                <dd>{connection.scope.split(' ').join(', ')}</dd>
                            </div>
                        )}
                        {isLinked && (
                            <>
                                <div className="pd-fact">
                                    <dt>Hub</dt>
                                    <dd>{displayRef(accLink.hub)}</dd>
                                </div>
                                <div className="pd-fact">
                                    <dt>ACC project</dt>
                                    <dd>{displayRef(accLink.project)}</dd>
                                </div>
                            </>
                        )}
                    </dl>

                    {linkBroken && (
                        <div className="pd-aps-broken" role="alert">
                            <h4 className="pd-aps-broken-title">{BROKEN_LINK_COPY[accLink.state].title}</h4>
                            <p className="pd-note">{BROKEN_LINK_COPY[accLink.state].body}</p>
                        </div>
                    )}

                    {linkUnverified && (
                        <p className="pd-note">
                            The link could not be checked just now, so what it points at is unconfirmed.
                            This is usually temporary. Nothing here needs fixing on that basis alone.
                        </p>
                    )}

                    {!isLinked && !isPicking && (
                        <p className="pd-note">
                            Pick the ACC project this one maps to. Connecting authorized your Autodesk
                            account, it did not say which project to read.
                        </p>
                    )}

                    {showPicker && (
                        <div className="pd-aps-picker">
                            <label className="pw-label" htmlFor="aps-hub">Hub</label>
                            <select
                                id="aps-hub"
                                className="zrf-select"
                                value={hubId}
                                onChange={e => {
                                    setHubId(e.target.value);
                                    setAccProjectId('');
                                    setReplacePrompt(false);
                                }}
                                disabled={aps.hubs.isLoading || aps.action.isBusy}
                            >
                                <option value="">
                                    {aps.hubs.isLoading ? 'Loading hubs…' : 'Select a hub'}
                                </option>
                                {aps.hubs.data.map(hub => (
                                    <option key={hub.id} value={hub.id}>{hub.name || hub.id}</option>
                                ))}
                            </select>
                            {aps.hubs.error && <p className="org-error">{apsErrorMessage(aps.hubs.error)}</p>}

                            <label className="pw-label" htmlFor="aps-acc-project">ACC project</label>
                            <select
                                id="aps-acc-project"
                                className="zrf-select"
                                value={accProjectId}
                                onChange={e => setAccProjectId(e.target.value)}
                                disabled={!hubId || aps.accProjects.isLoading || aps.action.isBusy}
                            >
                                <option value="">
                                    {!hubId
                                        ? 'Select a hub first'
                                        : aps.accProjects.isLoading
                                            ? 'Loading projects…'
                                            : 'Select an ACC project'}
                                </option>
                                {aps.accProjects.data.map(accProject => (
                                    <option key={accProject.id} value={accProject.id}>
                                        {accProject.name || accProject.id}
                                    </option>
                                ))}
                            </select>
                            {aps.accProjects.error && (
                                <p className="org-error">{apsErrorMessage(aps.accProjects.error)}</p>
                            )}

                            {replacePrompt && (
                                <p className="pd-note pd-aps-replace">
                                    This project is already linked to a different ACC project.
                                    Replacing it means everything read for this project comes from
                                    somewhere else.
                                </p>
                            )}

                            <div className="pw-actions">
                                {replacePrompt ? (
                                    <button
                                        type="button"
                                        className="rules-btn-primary pd-danger-btn"
                                        disabled={aps.action.isBusy}
                                        onClick={() => submitLink(true)}
                                    >
                                        {aps.action.isBusy ? 'Replacing…' : 'Replace link'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="rules-btn-primary"
                                        disabled={!hubId || !accProjectId || aps.action.isBusy}
                                        onClick={() => submitLink(false)}
                                    >
                                        {aps.action.isBusy ? 'Linking…' : 'Link project'}
                                    </button>
                                )}
                                {isPicking && (
                                    <button
                                        type="button"
                                        className="rules-btn-ghost"
                                        onClick={cancelPicking}
                                        disabled={aps.action.isBusy}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {actionError && <p className="org-error">{actionError}</p>}

                    <div className="pw-actions pd-aps-actions">
                        {isLinked && !isPicking && (
                            <button type="button" className="rules-btn-ghost" onClick={startPicking}>
                                Change link
                            </button>
                        )}
                        {isLinked && (
                            <button
                                type="button"
                                className="rules-btn-ghost"
                                onClick={handleUnlink}
                                disabled={aps.action.isBusy}
                            >
                                Unlink
                            </button>
                        )}
                        <button
                            type="button"
                            className="rules-btn-ghost pd-danger"
                            onClick={() => { dispatch(clearApsActionError(projectId)); setConfirmingDisconnect(true); }}
                        >
                            Disconnect
                        </button>
                    </div>
                </div>
            )}

            {isConfirmingDisconnect && (
                <div className="members-modal-backdrop" role="presentation">
                    <div
                        className="members-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="disconnect-aps-title"
                    >
                        <h2 id="disconnect-aps-title">Disconnect from Autodesk?</h2>
                        <p>
                            This revokes the stored access at Autodesk, not just here, so Arkognize will
                            no longer appear as an authorized application. The linked ACC project is
                            remembered, so reconnecting will not ask you to pick it again.
                        </p>
                        {actionError && <p className="org-error">{actionError}</p>}
                        <div className="pw-actions">
                            <button
                                type="button"
                                className="rules-btn-ghost"
                                onClick={() => setConfirmingDisconnect(false)}
                                disabled={aps.action.isBusy}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="members-remove-button"
                                onClick={handleDisconnect}
                                disabled={aps.action.isBusy}
                            >
                                {aps.action.isBusy ? 'Disconnecting…' : 'Disconnect'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
